/**
 * Life4Earth V2 — Mission Metrics API Adapter
 *
 * Replaces: import { missionMetricsData } from './Data/KpiEngine/missionMetricsData'
 *           import { missionTargets } from './Data/KpiEngine/missionTargets'
 * Endpoint: /api/MissionMetrics
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types ────────────────────────────────────────────────────

export interface MissionMetricPointResponse {
  id: string;
  // DEV-1075: projectId nullable to support Mini-Project-owned rows
  // (polymorphic ownership via mini_project_guid). Exactly one of the
  // two is populated per row.
  projectId: string | null;
  miniProjectGuid?: string | null;
  subKpiKey: string;
  reportingDate: string; // "YYYY-MM-DD"
  // DEV-1075: value nullable so qualitative-only MP rows can leave it
  // null and rely on .assessment instead.
  value: number | null;
  enteredAt: string | null;
  note: string | null;
  createdAt: string;
  /**
   * Optional Checklist item identifier (DEV-816 Option D).
   * When present, this point represents the completion of a SPECIFIC item
   * in a Checklist sub-KPI. Null for non-Checklist types and legacy data.
   */
  itemId?: string | null;
  /**
   * DEV-1075 Path A qualitative assessment.
   * 'ahead' | 'on_track' | 'behind' | 'blocked' | 'done'.
   * Set on Mini-Project rows; null on Full-Project rows.
   */
  assessment?: string | null;
}

export interface MissionTargetResponse {
  id: string;
  projectId: string;
  subKpiKey: string;
  goal: number | null;
  baseline: number | null;
  direction: string;
  unit: string | null;
  dueDate: string | null; // "YYYY-MM-DD"
  minimum: number | null;
  policy: string;
  isActive: boolean;
  assigneeGuid: string | null;
}

// ── Shape Converters ────────────────────────────────────────

/**
 * Convert API metric point to the seed-data shape that App.tsx
 * and computeKpisForProject expect.
 */
export function metricPointToSeedShape(
  p: MissionMetricPointResponse
): Record<string, any> {
  // DEV-1075: polymorphic owner — projectId on FP rows, miniProjectGuid
  // on MP rows. Engine groups by subKpiKey and doesn't currently split
  // by owner; passing whichever guid is set as project_GUID preserves
  // the existing engine contract.
  const ownerGuid = p.projectId ?? p.miniProjectGuid ?? null;
  return {
    project_GUID: normalizeGuid(ownerGuid ?? ''),
    subKpiKey: p.subKpiKey,
    date: p.reportingDate,
    // Engine MissionMetricPoint.value is `number` (not nullable); coerce
    // null → 0 for qualitative-only rows. The engine's qualitative branch
    // ignores value entirely (reads .assessment instead), so the 0 is
    // never consulted for MP qualitative scoring.
    value: p.value ?? 0,
    entered_at: p.enteredAt ?? undefined,
    note: p.note ?? undefined,
    // DEV-816 Option D: pass item_id through to the engine so Checklist
    // points can be grouped by item rather than aggregated as a count.
    item_id: p.itemId ?? undefined,
    // DEV-1075 Path A: pass assessment through so the engine's
    // qualitative scoring branch can read it.
    assessment: p.assessment ?? undefined,
  };
}

/**
 * Convert API targets to the seed-data shape: Record<projectGuid, Record<subKpiKey, MissionTarget>>
 */
export function targetsToSeedShape(
  targets: MissionTargetResponse[]
): Record<string, Record<string, any>> {
  const result: Record<string, Record<string, any>> = {};
  for (const t of targets) {
    const pgKey = normalizeGuid(t.projectId);
    if (!pgKey) continue;
    if (!result[pgKey]) {
      result[pgKey] = {};
    }
    const entry: Record<string, any> = {
      direction: t.direction,
      policy: t.policy,
    };
    if (t.goal != null) entry.goal = t.goal;
    if (t.baseline != null) entry.baseline = t.baseline;
    if (t.unit) entry.unit = t.unit;
    if (t.dueDate) entry.due_date = t.dueDate;
    if (t.minimum != null) entry.minimum = t.minimum;

    result[pgKey][t.subKpiKey] = entry;
  }
  return result;
}

// ── API Functions ────────────────────────────────────────────

/** Fetch ALL mission metric points across all projects. */
export async function fetchAllMissionMetricPoints(): Promise<
  MissionMetricPointResponse[]
> {
  return apiFetch<MissionMetricPointResponse[]>(
    "/api/MissionMetrics/points/all"
  );
}

/** Fetch ALL active mission targets across all projects. */
export async function fetchAllMissionTargets(): Promise<
  MissionTargetResponse[]
> {
  return apiFetch<MissionTargetResponse[]>(
    "/api/MissionMetrics/targets/all"
  );
}

/** Fetch mission metric points for a single project. */
export async function fetchMissionMetricPointsByProject(
  projectId: string,
  subKpiKey?: string
): Promise<MissionMetricPointResponse[]> {
  const params = new URLSearchParams({ projectId });
  if (subKpiKey) params.set("subKpiKey", subKpiKey);
  return apiFetch<MissionMetricPointResponse[]>(
    `/api/MissionMetrics/points?${params.toString()}`
  );
}

/**
 * DEV-1075 Phase 6 → 7.1 — fetch mission metric points for a single
 * Mini-Project. Parallel to fetchMissionMetricPointsByProject;
 * backed by the GET /api/MissionMetrics/points/by-mini-project
 * endpoint added in Phase 6.
 *
 * MP-side returns include both qualitative-only rows (value=null,
 * assessment set) and numeric snapshot/per-item rows (value set).
 * Phase 7.1's miniProjectGoalView derivation handles both signal
 * shapes; the Path A engine branch (Phase 5.1) reads assessment
 * for color/score and value for the progress-bar fill.
 */
export async function fetchMissionMetricPointsByMiniProject(
  miniProjectGuid: string,
  subKpiKey?: string
): Promise<MissionMetricPointResponse[]> {
  const params = new URLSearchParams({ miniProjectGuid });
  if (subKpiKey) params.set("subKpiKey", subKpiKey);
  return apiFetch<MissionMetricPointResponse[]>(
    `/api/MissionMetrics/points/by-mini-project?${params.toString()}`
  );
}

/** Fetch mission targets for a single project. */
export async function fetchMissionTargetsByProject(
  projectId: string
): Promise<MissionTargetResponse[]> {
  return apiFetch<MissionTargetResponse[]>(
    `/api/MissionMetrics/targets?projectId=${projectId}`
  );
}

// ── Write Types ──────────────────────────────────────────────

export interface CreateMissionMetricPointRequest {
  /**
   * DEV-1075 polymorphic ownership. Exactly one of projectId or
   * miniProjectGuid must be set. Backend DTO mirrors this — see
   * src/Life4Earth.V2.Api/DTOs/MissionMetricDtos.cs. Controller
   * validates and the chk_metric_points_owner XOR check enforces
   * at the DB level.
   */
  projectId?: string;
  miniProjectGuid?: string;
  subKpiKey: string;
  reportingDate: string; // "YYYY-MM-DD"
  value: number;
  note?: string;
  /**
   * Checklist item identifier (DEV-816 Option D).
   * Set when the point marks a specific Checklist item complete (value=1)
   * or incomplete. Omit for non-Checklist data points.
   */
  itemId?: string;
}

export interface UpdateMissionMetricPointRequest {
  value?: number;
  note?: string;
}

export interface UpsertMissionTargetRequest {
  projectId: string;
  subKpiKey: string;
  goal?: number;
  baseline?: number;
  direction?: string;
  unit?: string;
  dueDate?: string; // "YYYY-MM-DD"
  minimum?: number;
  policy?: string;
  assigneeGuid?: string;
}

// ── Write Functions ──────────────────────────────────────────

/** Create a new mission metric data point. */
export async function createMissionMetricPoint(
  request: CreateMissionMetricPointRequest
): Promise<MissionMetricPointResponse> {
  return apiFetch<MissionMetricPointResponse>(
    "/api/MissionMetrics/points",
    { method: "POST", body: JSON.stringify(request) }
  );
}

/** Update an existing mission metric data point. */
export async function updateMissionMetricPoint(
  id: string,
  request: UpdateMissionMetricPointRequest
): Promise<MissionMetricPointResponse> {
  return apiFetch<MissionMetricPointResponse>(
    `/api/MissionMetrics/points/${id}`,
    { method: "PUT", body: JSON.stringify(request) }
  );
}

/** Delete a mission metric data point (hard delete). */
export async function deleteMissionMetricPoint(
  id: string
): Promise<void> {
  return apiFetch<void>(`/api/MissionMetrics/points/${id}`, {
    method: "DELETE",
  });
}

/** Create or update a mission target (upsert by project + subKpiKey). */
export async function upsertMissionTarget(
  request: UpsertMissionTargetRequest
): Promise<MissionTargetResponse> {
  return apiFetch<MissionTargetResponse>(
    "/api/MissionMetrics/targets",
    { method: "POST", body: JSON.stringify(request) }
  );
}

/** Deactivate (soft delete) a mission target. */
export async function deactivateMissionTarget(
  id: string
): Promise<void> {
  return apiFetch<void>(`/api/MissionMetrics/targets/${id}`, {
    method: "DELETE",
  });
}
